"use client";

import { useRef, type ReactNode } from "react";
import Link from "next/link";

import { LogoOI } from "@/components/common/LogoOI";
import { EditorialeSettimana } from "@/components/fasce/EditorialeSettimana";
import { DATI_MONO, ETICHETTA_ACCESA, RIMANDO, TITOLO_LINK } from "@/components/home/stile";
import { PerApprofondireAperto } from "@/components/article/NotiziaAperta";
import { Anello, Imbuto, Posto } from "@/components/paesi/pagina/DallaFonte";
import { DisegnoServizio } from "@/components/servizi/catalogo/DisegnoServizio";
import { FUOCO } from "@/components/ui/pulsanti";
import { useRiempiVuoto } from "@/hooks/useRiempiVuoto";
import type { EditorialeRiquadro } from "@/lib/editoriale-settimana";
import { fmtDispatchId } from "@/lib/format";
import { etichettaLuogo } from "@/lib/nomiLuoghi";
import type { Approfondimento } from "@/lib/notizia";
import { servizioDi } from "@/lib/sorteggio-servizi";

/* I due riempitivi del vuoto fra due colonne, nati nella pagina del
   dispaccio aperto (components/article/DispaccioView.tsx) il 20/10/2026 e
   usciti di lì il 27/10/2026, quando anche la pagina di lettura degli
   editoriali del CMS (components/editoriali/LetturaEditoriale.tsx) ha preso
   lo stesso meccanismo: `RiempiTesto` sotto la colonna del testo quando la
   colonna accanto è più lunga, `ColonnaDiCoda` in fondo alla colonna
   accanto quando il testo è più lungo. Stanno in un modulo loro perché la
   pagina dell'editoriale non si porti dietro tutto DispaccioView. */

export type CodaSpalla = {
  editoriale: EditorialeRiquadro | null;
  report: Approfondimento[];
  /** Slug dei servizi da annunciare, già sorteggiati sul server. */
  annunci: string[];
  /** I due servizi del riempitivo sotto il testo (`RiempiTesto`). */
  sottoTesto: string[];
  /** Altri editoriali, in testa alla coda prima dei report: solo la
   *  pagina di un editoriale li passa (escluso quello aperto). */
  editoriali?: EditorialeRiquadro[];
  /** Le schede dei manuali (OSINT, cibernetica, AI, chi siamo) fra i
   *  report e gli annunci: solo la pagina di un editoriale le chiede, sul
   *  dispaccio aperto stanno già sotto il testo (`RiempiTesto`). */
  manuali?: boolean;
};

/** Le quattro schede dei manuali e del chi siamo, coi disegni di «Dalla
 *  fonte alla decisione» della pagina Paese. Una funzione e non una
 *  costante: servono a `RiempiTesto` e a `ColonnaDiCoda`. */
function schedeManuali(): { k: string; nodo: ReactNode }[] {
  return [
    {
      k: "osint",
      nodo: (
        <SchedaManuale etichetta="OSINT · Il metodo" titolo="Dalla fonte all’informazione utile." href="/osint" rimando="Leggi il manuale OSINT">
          <Imbuto />
        </SchedaManuale>
      ),
    },
    {
      k: "cib",
      nodo: (
        <SchedaManuale etichetta="Cibernetica aziendale" titolo="Il problema ce l’hai già. Il numero arriva dopo." href="/cibernetica" rimando="Scopri la cibernetica">
          <Anello />
        </SchedaManuale>
      ),
    },
    {
      k: "ai",
      nodo: (
        <SchedaManuale etichetta="AI · Percorso per PMI" titolo="Il sistema, prima dell’AI." href="/ai" rimando="Il percorso di adozione">
          <Posto />
        </SchedaManuale>
      ),
    },
    {
      k: "chi",
      nodo: (
        <SchedaManuale etichetta="Chi siamo" titolo="Una visione propria, i mezzi per costruirla." href="/chi-siamo" rimando="Conosci l’Osservatorio">
          <span className="my-1.5 flex aspect-[300/120] items-center justify-center border border-rule bg-[var(--oi-sv-fondo)]">
            <LogoOI variant="segno" decorative accento="fill-segnale" className="h-16 w-auto text-mare" />
          </span>
        </SchedaManuale>
      ),
    },
  ];
}

/* ── La coda della spalla (aperti) ──────────────────────────────── */

/** I report vicini al testo come una tabella sola, non una scheda per
 *  report (richiesta del 20/10/2026: «non va bene spezzettare le
 *  informazioni così, serve una tabella tecnica»). Colonne Data / Report /
 *  Paese, il codice OI sotto il titolo, filetti fra le righe. Le righe
 *  portano `data-riga`: se la tabella non sta tutta accanto al testo,
 *  `ColonnaDiCoda` ne toglie dal fondo invece di toglierla intera. */
export function TabellaReport({ righe }: { righe: Approfondimento[] }) {
  return (
    <section aria-labelledby="report-vicini" className="border border-ink border-t-[3px] border-t-mare bg-surface">
      <div className="flex items-baseline justify-between gap-4 border-b border-ink px-4 py-3">
        <h2 id="report-vicini" className={`m-0 ${ETICHETTA_ACCESA}`}>
          Report collegati
        </h2>
        <Link href="/report" className={`font-sans text-voce font-semibold text-mare no-underline hover:underline ${FUOCO}`}>
          Tutti i report <span aria-hidden>↗</span>
        </Link>
      </div>
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className={`border-b border-rule ${DATI_MONO}`}>
            <th scope="col" className="w-[76px] px-4 py-2 font-normal">Data</th>
            <th scope="col" className="py-2 pr-3 font-normal">Report</th>
            <th scope="col" className="w-[84px] py-2 pr-4 font-normal">Paese</th>
          </tr>
        </thead>
        <tbody>
          {righe.map((r) => (
            <tr key={r.id} data-riga className="border-b border-rule align-top last:border-b-0">
              <td className="px-4 py-2.5 font-mono text-meta uppercase tracking-dato text-sommesso">
                <time dateTime={r.data.iso}>{r.data.testo}</time>
              </td>
              <td className="py-2.5 pr-3">
                <Link href={r.href} className={`block font-serif text-[15px] leading-[1.3] ${TITOLO_LINK}`}>
                  {r.titolo}
                </Link>
                <span className="mt-1 block font-mono text-meta tabular-nums text-sommesso">{fmtDispatchId(r.id)}</span>
              </td>
              <td className="py-2.5 pr-4 font-mono text-meta uppercase tracking-dato text-ink">
                {r.territorio ? etichettaLuogo(r.territorio) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** Un annuncio della casa: un servizio col suo disegno del catalogo, o
 *  l'Accoglienza. Etichetta «Annuncio» in chiaro: è pubblicità nostra, e
 *  chi legge deve distinguerla dalle notizie. */
export function SchedaAnnuncio({ slug }: { slug: string }) {
  if (slug === "accoglienza") {
    return (
      <aside aria-label="Annuncio" className="flex flex-col gap-2.5 bg-fasciaMare px-[22px] py-5 text-suFasciaMare">
        <p className="m-0 font-sans text-voce font-semibold opacity-80">Annuncio · Accoglienza</p>
        <p className="m-0 font-serif text-[26px] leading-[1.1]">Chiamateci, 7 giorni su 7.</p>
        <p className="m-0 font-sans text-sommario leading-[1.45] opacity-90">
          Una persona, non un centralino. Quello che ci racconti resta riservato.
        </p>
        <Link href="/accoglienza" className={`mt-1 self-start font-sans text-corpo font-semibold text-suFasciaMare underline-offset-4 hover:underline ${FUOCO}`}>
          Apri l&rsquo;Accoglienza <span aria-hidden>↗</span>
        </Link>
      </aside>
    );
  }
  const s = servizioDi(slug);
  if (!s) return null;
  return (
    <aside aria-label="Annuncio" className="border border-ink bg-surface">
      <Link href={`/servizi/${s.slug}`} className={`group/a flex flex-col text-ink no-underline ${FUOCO}`}>
        <span className="block aspect-[360/150] border-b border-rule bg-[var(--oi-sv-fondo)]">
          <DisegnoServizio slug={s.slug} />
        </span>
        <span className="flex flex-col gap-1.5 px-[22px] pb-5 pt-4">
          <span className="font-sans text-voce font-semibold text-sommesso">Annuncio · Servizio {s.numero}</span>
          <span className="font-sans text-voce font-semibold text-segnale">{s.frase}</span>
          <b className="font-serif text-[22px] font-normal leading-[1.2] transition-colors group-hover/a:text-mare">{s.titolo}</b>
          <span className="line-clamp-3 font-sans text-sommario leading-[1.45] text-sommesso">{s.descrizione}</span>
          <span className="mt-1 font-sans text-corpo font-semibold text-mare">
            Scopri il servizio <span aria-hidden>↗</span>
          </span>
        </span>
      </Link>
    </aside>
  );
}

/** Il riempitivo sotto il testo, quando la spalla è più lunga del testo
 *  (richiesta del committente del 20/10/2026: «quando il testo finisce c'è
 *  un bianco, riempirlo con una pubblicità, un servizio, AI, cibernetica,
 *  il chi siamo, con tutte le forme del sito»). Stesso meccanismo della
 *  coda della spalla (`useRiempiVuoto`): ci stanno quante schede servono
 *  a chiudere il bianco, mai una di più, e la pagina non si allunga.
 *  Le forme sono quelle del sito: la scheda servizio col suo disegno del
 *  catalogo (`DisegnoServizio`), i tre manuali coi disegni di «Dalla fonte
 *  alla decisione» della pagina Paese (`Imbuto`, `Anello`, `Posto`), il
 *  segno OI per «Chi siamo». Solo da `lg`. */
export function RiempiTesto({
  servizi,
  editoriali = [],
  vicini = [],
  paese = null,
  staccoDentro = false,
}: {
  servizi: string[];
  /** Solo la pagina di un editoriale: altri editoriali, due per riga, in
   *  testa a tutto (sono i contenuti più vicini al pezzo). */
  editoriali?: EditorialeRiquadro[];
  /** Solo la pagina di un editoriale: i dispacci dello stesso paese, degli
   *  stessi soggetti o dello stesso tema (`approfondimentiPer`), nella
   *  forma di «Per approfondire» del dispaccio aperto. */
  vicini?: Approfondimento[];
  paese?: string | null;
  /** Lo stacco di 40 pixel dal testo dentro la scatola invece che come
   *  margine: quando il testo è più lungo della colonna accanto la scatola
   *  è alta zero, e il margine allungherebbe la riga di 40 pixel di niente.
   *  Lo chiede la pagina di un editoriale; il dispaccio resta com'era. */
  staccoDentro?: boolean;
}) {
  const scatola = useRef<HTMLDivElement>(null);
  const [primo, secondo] = servizi;
  const schede: { k: string; largo?: boolean; nodo: ReactNode }[] = [];
  for (const e of editoriali) {
    schede.push({ k: `e${e.href}`, nodo: <EditorialeSettimana e={e} colonna etichetta="Editoriale" /> });
  }
  // Gli editoriali stanno due per riga: se sono dispari, la riga la chiude
  // il primo manuale, altrimenti la scheda larga che segue lascerebbe mezza
  // riga vuota (la griglia non torna indietro a riempirla).
  const manuali = schedeManuali();
  if (editoriali.length % 2 === 1) schede.push(manuali.shift()!);
  if (vicini.length > 0) {
    schede.push({ k: "vicini", largo: true, nodo: <PerApprofondireAperto voci={vicini} paese={paese} /> });
  }
  if (primo) schede.push({ k: `s${primo}`, largo: true, nodo: <ServizioLargo slug={primo} /> });
  schede.push(...manuali);
  // In coda le forme via via più basse (servizio ~230, Accoglienza ~110,
  // striscia dei rimandi ~55): dove le grandi non entrano, chiudono il
  // bianco quelle piccole, e OSINT, cibernetica, AI e chi siamo restano
  // comunque in pagina con la striscia.
  if (secondo) schede.push({ k: `s${secondo}`, largo: true, nodo: <ServizioLargo slug={secondo} /> });
  schede.push({ k: "acc", largo: true, nodo: <FasciaAccoglienza /> });
  schede.push({ k: "strip", largo: true, nodo: <StrisciaRimandi /> });
  useRiempiVuoto(scatola);
  return (
    <div
      ref={scatola}
      aria-label="Dall’Osservatorio"
      role="region"
      className={`relative hidden flex-1 lg:block ${staccoDentro ? "" : "mt-10"}`}
    >
      <div className={`absolute inset-x-0 bottom-0 grid ${staccoDentro ? "top-10" : "top-0"} grid-cols-2 content-start gap-6 overflow-hidden`}>
        {schede.map((x) => (
          <div key={x.k} className={x.largo ? "col-span-2" : ""}>
            {x.nodo}
          </div>
        ))}
      </div>
    </div>
  );
}

/** I rimandi alle pagine del metodo in una riga sola, con il segno OI:
 *  la forma più bassa del riempitivo. */
const RIMANDI_OSSERVATORIO: [string, string][] = [
  ["OSINT", "/osint"],
  ["Cibernetica", "/cibernetica"],
  ["AI per le PMI", "/ai"],
  ["La lavagna", "/lavagna"],
  ["Chi siamo", "/chi-siamo"],
  ["Accoglienza 7/7", "/accoglienza"],
];
/** L'Accoglienza in una fascia bassa a tutta larghezza della colonna. */
function FasciaAccoglienza() {
  return (
    <aside
      aria-label="Annuncio"
      className="flex flex-wrap items-center justify-between gap-x-8 gap-y-3 bg-fasciaMare px-6 py-5 text-suFasciaMare"
    >
      <span className="flex flex-col gap-1">
        <span className="font-sans text-voce font-semibold opacity-80">Annuncio · Accoglienza 7/7</span>
        <span className="font-serif text-[26px] leading-[1.1]">Chiamateci, 7 giorni su 7.</span>
      </span>
      <Link
        href="/accoglienza"
        className={`font-sans text-corpo font-semibold text-suFasciaMare underline-offset-4 hover:underline ${FUOCO}`}
      >
        Una persona, non un centralino <span aria-hidden>↗</span>
      </Link>
    </aside>
  );
}

function StrisciaRimandi() {
  return (
    <nav aria-label="Dall’Osservatorio" className="flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-ink px-1 py-3.5">
      <LogoOI variant="segno" decorative accento="fill-segnale" className="h-4 w-auto text-mare" />
      {RIMANDI_OSSERVATORIO.map(([t, h]) => (
        <Link key={h} href={h} className={RIMANDO}>
          {t} <span aria-hidden>↗</span>
        </Link>
      ))}
    </nav>
  );
}

/** Un manuale o una pagina del sito come scheda: etichetta rame, titolo
 *  serif, il disegno, il rimando. */
export function SchedaManuale({
  etichetta,
  titolo,
  href,
  rimando,
  children,
}: {
  etichetta: string;
  titolo: string;
  href: string;
  rimando: string;
  children: ReactNode;
}) {
  return (
    <article className="flex h-full flex-col gap-2 border border-ink border-t-[3px] border-t-segnale bg-surface px-5 pb-5 pt-4">
      <p className={`m-0 ${ETICHETTA_ACCESA}`}>{etichetta}</p>
      <h3 className="m-0 font-serif text-[21px] font-normal leading-[1.2] text-ink">
        <Link href={href} className={TITOLO_LINK}>
          {titolo}
        </Link>
      </h3>
      {children}
      <Link href={href} className={`mt-auto self-start ${RIMANDO}`}>
        {rimando} <span aria-hidden>↗</span>
      </Link>
    </article>
  );
}

/** Un servizio a tutta larghezza della colonna del testo: disegno del
 *  catalogo a sinistra, testi a destra. Marcato «Annuncio». */
function ServizioLargo({ slug }: { slug: string }) {
  const s = servizioDi(slug);
  if (!s) return null;
  return (
    <aside aria-label="Annuncio" className="border border-ink bg-surface">
      <Link href={`/servizi/${s.slug}`} className={`group/a grid grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] text-ink no-underline ${FUOCO}`}>
        <span className="block border-r border-rule bg-[var(--oi-sv-fondo)]">
          <span className="block aspect-[360/150]">
            <DisegnoServizio slug={s.slug} />
          </span>
        </span>
        <span className="flex flex-col gap-1.5 px-6 py-5">
          <span className="font-sans text-voce font-semibold text-sommesso">Annuncio · Servizio {s.numero}</span>
          <span className="font-sans text-voce font-semibold text-segnale">{s.frase}</span>
          <b className="font-serif text-[24px] font-normal leading-[1.15] transition-colors group-hover/a:text-mare">{s.titolo}</b>
          <span className="line-clamp-2 font-sans text-sommario leading-[1.45] text-sommesso">{s.descrizione}</span>
          <span className="mt-1 font-sans text-corpo font-semibold text-mare">
            Scopri il servizio <span aria-hidden>↗</span>
          </span>
        </span>
      </Link>
    </aside>
  );
}

/**
 * La coda della spalla di una notizia aperta: l'editoriale della
 * settimana, la tabella dei report vicini al testo e qualche annuncio.
 * Richiesta del committente del 20/10/2026: la spalla finiva con «Dai
 * nostri servizi» e sotto, accanto a un testo lungo, restava il vuoto.
 *
 * Quante schede: tante quante ne stanno accanto al testo. Da `lg` la
 * griglia stira la spalla all'altezza della colonna del testo, e la coda
 * sta in una scatola `flex-1` con dentro un livello `absolute inset-0`:
 * così le schede non entrano nel conto dell'altezza della riga (la pagina
 * non si allunga per colpa loro, vedi LEARNINGS sugli `svg` in colonne
 * flex) e dopo l'idratazione si nascondono quelle che sforano. Nasconderle
 * non sposta niente, perché la scatola ha già la sua altezza: zero salti.
 * Un testo corto non lascia posto e la coda resta vuota; uno lungo ne
 * mostra fino a otto. Sotto `lg` la spalla va sotto il testo, il vuoto
 * non c'è e la coda non si rende.
 */
export function ColonnaDiCoda({
  coda,
  staccoDentro = false,
}: {
  coda: CodaSpalla;
  /** Come in `RiempiTesto`: la colonna mette 40 pixel (`gap-10`) prima
   *  della coda anche quando la coda è alta zero. Con `staccoDentro` la
   *  scatola risale di 40 e il livello delle schede parte 40 più in basso:
   *  una coda vuota non allunga la colonna. */
  staccoDentro?: boolean;
}) {
  const scatola = useRef<HTMLDivElement>(null);
  const r = coda.report;
  const a = coda.annunci;
  // L'ordine: editoriale (della settimana, poi gli altri), la tabella dei
  // report vicini al testo, i manuali se chiesti, poi gli annunci.
  const schede: { k: string; nodo: ReactNode }[] = [];
  if (coda.editoriale) schede.push({ k: "ed", nodo: <EditorialeSettimana e={coda.editoriale} colonna /> });
  for (const e of coda.editoriali ?? []) {
    schede.push({ k: `e${e.href}`, nodo: <EditorialeSettimana e={e} colonna etichetta="Editoriale" /> });
  }
  if (r.length) schede.push({ k: "report", nodo: <TabellaReport righe={r} /> });
  if (coda.manuali) schede.push(...schedeManuali());
  for (const slug of a) schede.push({ k: `a${slug}`, nodo: <SchedaAnnuncio slug={slug} /> });
  // Con i manuali, in coda la striscia dei rimandi, la forma più bassa:
  // chiude il bianco dove nessuna scheda intera entra più.
  if (coda.manuali) schede.push({ k: "strip", nodo: <StrisciaRimandi /> });

  useRiempiVuoto(scatola);

  if (schede.length === 0) return null;
  return (
    <div
      ref={scatola}
      aria-label="Da leggere ancora"
      role="region"
      className={`relative hidden flex-1 lg:block ${staccoDentro ? "-mt-10" : ""}`}
    >
      <div className={`absolute inset-x-0 bottom-0 flex flex-col gap-10 overflow-hidden ${staccoDentro ? "top-10" : "top-0"}`}>
        {schede.map((x) => (
          <div key={x.k}>{x.nodo}</div>
        ))}
      </div>
    </div>
  );
}
